import { uploadBlob } from '../api/blobUpload';
import { sedrApi } from '../api/sedr';
import { wrapKey } from './wrappedKey';
import { decodeBase64, encodeBase64, encodeBase64Url, randomBytes } from './primitives';
import { deriveProofKeys } from './infoShareFragment';
import {
  encryptSedrGuestEnvelope,
  SEDR_GUEST_ENVELOPE_AAD,
} from './sedrGuestInvite';
import {
  buildSedrGuestFragment,
  type SedrGuestFragment,
} from './sedrGuestFragment';

export type CreateSedrGuestInviteResult =
  | { outcome: 'inviteCreated'; guestUrl: string; guestInviteId: string }
  | { outcome: 'guestAdded'; userId: string; displayName: string }
  | { outcome: 'staffRejected'; reason: 'staffDomain' | 'staffUser' };

export async function lookupSedrGuestEmail(
  roomId: string,
  email: string,
): Promise<{
  outcome: string;
  userId?: string;
  userPublicKey?: string;
  displayName?: string;
  organization?: string;
}> {
  return sedrApi.lookupGuestEmail(roomId, email);
}

export async function createSedrAccountGuestInvite(input: {
  roomId: string;
  roomKey: Uint8Array;
  displayName: string;
  organization: string;
  email: string;
  expiresAt?: string | null;
  guestRole?: string;
  allowCommenting?: boolean;
}): Promise<CreateSedrGuestInviteResult> {
  const lookup = await lookupSedrGuestEmail(input.roomId, input.email);
  if (lookup.outcome === 'staffDomain' || lookup.outcome === 'staffUser') {
    return { outcome: 'staffRejected', reason: lookup.outcome };
  }

  if (lookup.outcome === 'activeGuest' && lookup.userId && lookup.userPublicKey) {
    const userPublicKey = decodeBase64(lookup.userPublicKey);
    const wrappedRoomKey = await wrapKey(
      input.roomKey,
      userPublicKey,
      `shareweb:sedr:member:${input.roomId}`,
    );
    const created = await sedrApi.createGuestInvite(input.roomId, {
      displayName: input.displayName,
      organization: input.organization,
      email: input.email,
      guestRole: input.guestRole ?? 'GuestReader',
      allowCommenting: Boolean(input.allowCommenting),
      accessMode: 'AccountInvite',
      passwordMode: 'None',
      existingGuestUserId: lookup.userId,
      wrappedRoomKey: encodeBase64(wrappedRoomKey),
      expiresAt: input.expiresAt ?? null,
    });
    if (created.outcome === 'guestAdded') {
      return {
        outcome: 'guestAdded',
        userId: created.userId ?? lookup.userId,
        displayName: created.displayName ?? lookup.displayName ?? input.displayName,
      };
    }
    throw new Error('unexpected_invite_outcome');
  }

  const proofSeed = randomBytes(32);
  const { publicKey: proofPublicKey } = deriveProofKeys(proofSeed);
  const guestWrapKey = randomBytes(32);
  const envelopeBlob = await encryptSedrGuestEnvelope(input.roomKey, guestWrapKey);
  const envelopeBlobId = await uploadBlob(envelopeBlob);

  const created = await sedrApi.createGuestInvite(input.roomId, {
    displayName: input.displayName,
    organization: input.organization,
    email: input.email,
    envelopeBlobId,
    fragmentProofPublicKey: encodeBase64Url(proofPublicKey),
    passwordMode: 'None',
    accessMode: 'AccountInvite',
    expiresAt: input.expiresAt ?? null,
    guestRole: input.guestRole ?? 'GuestReader',
    allowCommenting: Boolean(input.allowCommenting),
  });

  if (created.outcome !== 'inviteCreated' || !created.guestPath || !created.guestInviteId) {
    throw new Error('unexpected_invite_outcome');
  }

  const fragmentPayload: SedrGuestFragment = {
    v: 1,
    ps: encodeBase64Url(proofSeed),
    gw: encodeBase64Url(guestWrapKey),
  };
  const fragment = buildSedrGuestFragment(fragmentPayload);
  const guestUrl = `${window.location.origin}${created.guestPath}#${fragment}`;
  return {
    outcome: 'inviteCreated',
    guestUrl,
    guestInviteId: created.guestInviteId,
  };
}

export { SEDR_GUEST_ENVELOPE_AAD };
